import { useCallback, useEffect, useState } from 'react';
import { IconAlertTriangle, IconExternalLink, IconLoader2 } from '@tabler/icons-react';
import { dxBoardAPI } from '../../api/client';
import { COLORS, RADIUS, badge } from '../../utils/uiStyles';
import { safeUrl } from '../../utils/safeUrl';

const sectionStyle = {
  background: COLORS.surface,
  border: `1px solid ${COLORS.borderSoft}`,
  borderRadius: RADIUS.lg,
  padding: '15px 16px',
};

const TONE_MAP = { default: 'gray', info: 'blue', success: 'green', warn: 'yellow', danger: 'red' };

function badgeStyle(tone = 'default') {
  return { ...badge(TONE_MAP[tone] || 'gray') };
}

function statusTone(status) {
  if (status === '보완요청') return 'danger';
  if (status === '배포요청') return 'info';
  return 'default';
}

// 시스템 배지는 DxWeekly 와 같은 규칙 — 2.0 은 파랑, 1.0 은 회색.
function systemTone(name) {
  return name === '샵메이트 2.0' ? 'info' : 'default';
}

// 경과일이 길수록 강하게. 당일 마감은 아직 지연이 아니므로 색을 달리한다.
function overdueTone(days) {
  if (days <= 0) return 'info';
  if (days >= 14) return 'danger';
  return 'warn';
}

function overdueLabel(days) {
  if (days <= 0) return '오늘 마감';
  return `${days}일 경과`;
}

function formatDate(value) {
  if (!value) return '-';
  const parts = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return parts ? `${parts[2]}/${parts[3]}` : '-';
}

function formatSyncedAt(value) {
  if (!value) return null;
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function peopleText(card) {
  const names = [...(card.assignees || [])];
  if (names.length) return `개발 ${names.join(', ')}`;
  const requesters = card.requesters || [];
  if (requesters.length) return `요청 ${requesters.join(', ')}`;
  return '담당자 없음';
}

function CardRow({ card, index }) {
  const href = safeUrl(card.url);
  const days = Number(card.overdue_days || 0);
  return (
    <li style={{ borderTop: index === 0 ? 'none' : `1px solid ${COLORS.borderSoft}`, padding: '10px 0', minWidth: 0 }}>
      <div style={{ display: 'flex', gap: '8px', minWidth: 0 }}>
        <span style={{ width: '22px', flexShrink: 0, textAlign: 'right', color: COLORS.textMuted, fontSize: '12px', fontWeight: 620, lineHeight: 1.55 }}>
          {index + 1}.
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 0 }}>
            {card.unique_id && (
              <span style={{ color: COLORS.textMuted, fontSize: '12px', fontWeight: 620, flexShrink: 0 }}>{card.unique_id}</span>
            )}
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                style={{ color: COLORS.text, textDecoration: 'none', fontWeight: 620, fontSize: '13px', lineHeight: 1.45, display: 'inline-flex', alignItems: 'center', gap: '4px', minWidth: 0 }}
              >
                <span style={{ wordBreak: 'break-word' }}>{card.title}</span>
                <IconExternalLink size={13} stroke={1.8} style={{ flexShrink: 0 }} />
              </a>
            ) : (
              <span style={{ color: COLORS.text, fontWeight: 620, fontSize: '13px', lineHeight: 1.45, wordBreak: 'break-word' }}>{card.title}</span>
            )}
            <span style={badgeStyle(overdueTone(days))}>{overdueLabel(days)}</span>
            <span style={badgeStyle(statusTone(card.status))}>{card.status || '상태 없음'}</span>
          </div>
          <div style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', color: COLORS.textSub, fontSize: '12px', lineHeight: 1.5 }}>
            <span>{peopleText(card)}</span>
            <span style={{ color: COLORS.textMuted, wordBreak: 'keep-all' }}>
              {`개발완료 ${formatDate(card.dev_done_at)} · QA완료 ${formatDate(card.qa_done_at)} · 배포 ${formatDate(card.deploy_planned_at)}`}
            </span>
          </div>
        </div>
      </div>
    </li>
  );
}

export default function DxOverdue({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    dxBoardAPI
      .getOverdue()
      .then((r) => setData(r.data))
      .catch(() => setError('일정 경과 카드를 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading && !data) {
    return (
      <div style={{ ...sectionStyle, display: 'flex', alignItems: 'center', gap: '8px', color: COLORS.textSub, fontSize: '13px' }}>
        <IconLoader2 size={16} stroke={2} style={{ animation: 'dx-summary-spin 1s linear infinite' }} />
        불러오는 중…
      </div>
    );
  }
  if (error) {
    return <div style={{ ...sectionStyle, color: COLORS.dangerText, fontSize: '13px', fontWeight: 700 }}>{error}</div>;
  }
  if (!data) return null;

  const syncedAt = formatSyncedAt(data.synced_at);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ ...sectionStyle, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <IconAlertTriangle size={18} stroke={1.9} style={{ color: data.total ? COLORS.warn : COLORS.textMuted, flexShrink: 0 }} />
        <span style={{ fontSize: '14px', fontWeight: 620, color: COLORS.text }}>
          일정 경과 {data.total}건
        </span>
        <span style={{ fontSize: '12px', color: COLORS.textSub }}>
          {data.groups.map((g) => `${g.label} ${g.count}`).join(' · ')}
        </span>
        {(data.system_counts || []).length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
            {data.system_counts.map((item) => (
              <span key={item.name} style={badgeStyle(systemTone(item.name))}>{`${item.name} ${item.count}`}</span>
            ))}
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: COLORS.textMuted }}>
          기준일 {data.today}{syncedAt ? ` · 노션 동기화 ${syncedAt}` : ''}
        </span>
      </div>

      {data.groups.map((group) => (
        <section key={group.key} style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', marginBottom: group.count ? '6px' : 0 }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 620, color: COLORS.text }}>
              {group.label}
            </h3>
            <span style={badgeStyle(group.count ? 'warn' : 'default')}>{group.count}건</span>
            <span style={{ fontSize: '12px', color: COLORS.textMuted, wordBreak: 'keep-all' }}>{group.desc}</span>
          </div>
          {group.count === 0 ? (
            <div style={{ color: COLORS.textMuted, fontSize: '13px', padding: '6px 0' }}>해당 카드가 없습니다.</div>
          ) : (
            (group.by_system || []).map((bucket, bucketIndex) => (
              <div key={bucket.name} style={{ marginTop: bucketIndex === 0 ? '2px' : '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingBottom: '2px' }}>
                  <span style={badgeStyle(systemTone(bucket.name))}>{bucket.name}</span>
                  <span style={{ fontSize: '12px', fontWeight: 620, color: COLORS.textSub }}>{bucket.count}건</span>
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {bucket.cards.map((card, index) => (
                    <CardRow key={card.page_id} card={card} index={index} />
                  ))}
                </ul>
              </div>
            ))
          )}
        </section>
      ))}

      <div style={{ color: COLORS.textMuted, fontSize: '12px', lineHeight: 1.6 }}>
        예정일이 오늘이거나 지났는데 상태가 그 단계까지 오지 못한 카드입니다.
        보류·Pin·완료·취소·보완요청 카드는 제외하며, 개발완료와 QA완료가 모두 지난 카드는 앞을 막고 있는
        &lsquo;개발 완료 지연&rsquo;에만 표시합니다.
      </div>
    </div>
  );
}
